'use client';

import React, { FormEvent, useState } from 'react';
import { IconX, IconZoom } from '@tabler/icons-react';
import { Button, List, TextInput } from '@mantine/core';
import { useMapContext } from '@/contexts/MapContext';
import classes from './finder.module.css';

const AWS_API_KEY = process.env.NEXT_PUBLIC_AWS_LOCATION_SERVICES_API_KEY ?? '';
const AWS_REGION = process.env.NEXT_PUBLIC_REGION ?? 'eu-west-2';

function get_zoom(placeType: string): number {
  switch (placeType.toLowerCase()) {
    case 'region':
      return 8;
    case 'subregion':
      return 9;
    case 'locality':
      return 11;
    case 'pointofinterest':
      return 13;
    case 'street':
    case 'postalcode':
      return 15;
    default:
      return 5;
  }
}

export function Finder() {
  const { map } = useMapContext();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<any[]>([]);
  const [, setSelectedIndex] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSelect = (selected: any) => {
    if (map) {
      const coordinates = selected?.Postion ? selected.Position : selected;
      const newZoom = get_zoom(coordinates.PlaceType);
      map?.jumpTo({
        center: coordinates.Position,
        zoom: newZoom,
      });
    }
  };

  async function triggerSearch() {
    if (!query) {
      return;
    }
    setLoading(true);
    setError(null);
    setResults([]);
    setSelectedIndex(null);

    try {
      const url = `https://places.geo.${AWS_REGION}.amazonaws.com/v2/geocode?key=${AWS_API_KEY}`;
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ QueryText: query }),
      });
      if (!response.ok) {
        throw new Error(`Error: ${response.statusText}`);
      }
      const data = await response.json();
      const fetchedResults = data.ResultItems || [];
      setResults(fetchedResults);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleSearch(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    await triggerSearch();
  }

  function handleIconClick() {
    if (!loading && query) {
      triggerSearch();
    }
  }

  function handleSelect(index: number) {
    setSelectedIndex(index);
    const selected = results[index];
    setQuery(selected.Address.Label);
    if (onSelect) {
      onSelect(selected);
    }
    setResults([]);
  }

  const clickableIcon = (
    <IconZoom
      onClick={handleIconClick}
      style={{ cursor: 'pointer', background: 'none', border: 'none', padding: 0 }}
      size={16}
    />
  );

  return (
    <div>
      <form onSubmit={handleSearch}>
        <TextInput
          className={classes.searchBox}
          leftSection={clickableIcon}
          placeholder="Find an address or place"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </form>
      {error && <div style={{ color: 'red' }}>{error}</div>}

      {results.length > 0 && (
        <div className={classes.searchResults}>
          <div style={{ flex: 1 }}>
            <List spacing={0} className={classes.searchList}>
              {results.map((result, index) => (
                <List.Item
                  key={index}
                  onClick={() => handleSelect(index)}
                  className={classes.searchListItem}
                >
                  {result.Address.Label.replace(/['"]/g, '')}
                </List.Item>
              ))}
            </List>
          </div>
          <div style={{ marginLeft: '1rem' }}>
            <Button
              type="button"
              onClick={() => {
                setResults([]);
                setSelectedIndex(null);
              }}
              className={classes.clearButton}
              aria-label="Clear results"
              variant="outline"
              size="xs"
            >
              <IconX size={16} />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
